import { MouseEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { MenuAction } from '@/components/Menu/Actions';
import { I18N_KEYS } from '@/language/keys';
import { useNodeFiltersStore } from '@/state/nodes/nodes.state';
import { SortIcon } from '@/UI/Icons/SortIcon';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';

type SourceTypeFilter = 'all' | 'tools_only' | 'models_only';

const SOURCE_FILTER_OPTIONS: Array<{
  value: SourceTypeFilter;
  translationKey: keyof typeof I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.MODE_TOGGLE;
}> = [
  { value: 'all', translationKey: 'ALL' },
  { value: 'tools_only', translationKey: 'TOOLS' },
  { value: 'models_only', translationKey: 'MODELS' },
];

export const FiltersMenu = () => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation();
  const { filters, setOrderBy, setSource } = useNodeFiltersStore();

  const items: MenuAction[] = useMemo(
    () => [
      {
        type: 'section_label',
        text: t(I18N_KEYS.GENERAL.FILTER),
      },
      {
        type: 'slot',
        el: (
          <ParentMenuItem id="type" label={t(I18N_KEYS.GENERAL.TYPE)} contextMenuWidth="160px">
            {SOURCE_FILTER_OPTIONS.map((option) => (
              <AppMenuItem
                key={option.value}
                onClick={() => setSource(option.value)}
                selected={filters?.source === option.value}
              >
                {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.MODE_TOGGLE[option.translationKey])}
                {filters?.source === option.value && <CheckMarkIcon />}
              </AppMenuItem>
            ))}
          </ParentMenuItem>
        ),
      },
      {
        type: 'divider',
      },
      {
        type: 'section_label',
        text: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.LABEL),
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.FEATURED),
        action: () => setOrderBy('featured'),
        selected: filters?.orderBy === 'featured',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.PRICE_ASC),
        action: () => setOrderBy('price_asc'),
        selected: filters?.orderBy === 'price_asc',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.PRICE_DESC),
        action: () => setOrderBy('price_desc'),
        selected: filters?.orderBy === 'price_desc',
      },
    ],
    [filters?.orderBy, filters?.source, setOrderBy, setSource, t],
  );

  return (
    <>
      <AppIconButton onClick={() => setMenuAnchorEl(filterBtnRef.current)} ref={filterBtnRef} width={24} height={24}>
        <SortIcon width={16} height={16} />
      </AppIconButton>
      <AppContextMenu
        width="160px"
        noClose
        open={!!menuAnchorEl}
        onClose={(e: MouseEvent) => {
          if ('stopPropagation' in e) {
            e.stopPropagation();
          }
          setMenuAnchorEl(null);
        }}
        anchorEl={menuAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        items={items}
      />
    </>
  );
};
